// The ring of the tab "Maschine" while water runs without a shot, as the round display draws it (firmware
// lib/RoundDisplay RoundDisplayUi.cpp drawStopwatch, drawFlushSteps, drawBackflush, drawDescale, drawWaterTankEmpty;
// the shot's dial is core/screens/dial.ts): the rinse after a shot fills it once in its 10 s, a flush fills one segment
// per pulse, a backflush and the descaling one per cycle or round, the empty tank a dim ring with the drop.
// Before (22.10.2026) the ring showed the temperature then, and the rinse not at all (Dominik: "auch spülen zeigt der
// Kreis nicht das richtige an").
import { presenceOf, localTime } from '../calm.js';
import { backflushProgressNote, liveOf, opOf, type Live } from '../status.js';
import type { ScreenContext } from './context.js';
import { backflushTimes } from './device.js';
import { tempRow, type DialRing, type DialTone, type DialView } from './dial.js';
import { formatNumber } from './format.js';
import { LIVE_RUN_ON_S } from './machine.js';

// the firmware's timings (lib/Orione: OrioneCare.h RinseAfterShot, OrioneWarmupFlush.h, OrioneDescale.h)
export const RINSE_MAX_S = 10;
export const FLUSH_PULSE_S = 3;
export const FLUSH_PAUSE_S = 20;
export const DESCALE_ROUNDS = 8;

/** The brew time of the running water and when it was measured; it runs on with this device's clock. */
function runClock(ctx: ScreenContext, state: number): { t: number; at: number } {
  const raw = ctx.view.live;
  const data = raw?.data as Live | undefined;
  if (raw && data?.state === state) return { t: data.brewTime ?? 0, at: localTime(ctx.view, raw.receivedAt) };
  // op said first that it runs: from then on (the live value follows within a second)
  const op = ctx.view.groups.op;
  return { t: 0, at: op ? localTime(ctx.view, op.receivedAt) : ctx.now };
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/** A dial of water: no second number, no sup, no icon unless given. */
function water(
  kind: DialView['kind'],
  label: string,
  labelTone: DialTone,
  value: string,
  unit: string | null,
  rows: ({ text: string; tone: DialTone } | null)[],
  ring: DialRing,
  share: number,
  icon: DialView['icon'] = null,
): DialView {
  return {
    kind,
    label,
    labelTone,
    check: false,
    value,
    unit,
    sup: null,
    second: null,
    rows: rows.filter((r): r is { text: string; tone: DialTone } => r !== null),
    icon,
    ring,
    share,
  };
}

/** Segments of a programme: the ones done full, the running one in the half colour (drawBackflush, drawDescale). */
const programParts = (n: number, current: number, allDone: boolean) =>
  Array.from({ length: n }, (_, i) =>
    allDone || i + 1 < current
      ? { fill: 1, current: false }
      : i + 1 === current
        ? { fill: 1, current: true }
        : { fill: 0, current: false },
  );

/**
 * What the ring shows while water runs without a shot (or the tank is empty), as the display; null otherwise (then
 * the temperature, or the shot). `now` is this device's clock of the frame: the seconds and the filling run on from
 * the last value for at most LIVE_RUN_ON_S, as the shot's ring does (liveEstimate).
 */
export function activityRing(ctx: ScreenContext, now: number = ctx.now): DialView | null {
  const presence = presenceOf(ctx.view, ctx.now);
  if (presence === 'offline' || presence === 'restarting' || presence === 'unknown') return null;
  const live = liveOf(ctx.view, ctx.now);
  const op = opOf(ctx.view);
  const state = live?.state ?? op.state;
  const seconds = (s: number) => {
    const c = runClock(ctx, s);
    return c.t + Math.min(LIVE_RUN_ON_S, Math.max(0, (now - c.at) / 1000));
  };
  const temp = tempRow(ctx);

  // the descaling first: its programme holds the machine in the flush state (state 25)
  const ds = op.descale;
  const dsPhase = live?.dsc ?? ds?.phase ?? 0;
  if (dsPhase > 0 && dsPhase < 7) {
    const round = Math.min(DESCALE_ROUNDS, Math.max(0, live?.dsr ?? ds?.round ?? 0));
    const pass = live?.dsp ?? ds?.pass ?? 0;
    const pump = live?.dsf ?? ds?.pump ?? false;
    // the time left counts down between two values as well
    const left0 = live?.dst ?? ds?.remaining ?? -1;
    const raw = ctx.view.live;
    const since = raw
      ? Math.min(LIVE_RUN_ON_S, Math.max(0, (now - localTime(ctx.view, raw.receivedAt)) / 1000))
      : 0;
    const left = left0 >= 0 ? Math.max(0, Math.ceil(left0 - since)) : -1;
    const mmss = left >= 0 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : null;
    const after = dsPhase >= 4;
    const t = live?.currentTemp;
    const value =
      dsPhase === 1
        ? typeof t === 'number'
          ? `${formatNumber(t, 0, ctx.lang)}°`
          : ''
        : dsPhase === 2
          ? `${round}/${DESCALE_ROUNDS}`
          : dsPhase === 6
            ? `${pass}/2`
            : '';
    const rows: ({ text: string; tone: DialTone } | null)[] =
      dsPhase === 1
        ? [{ text: ctx.tr('dsCoolingShort'), tone: 'dim' }]
        : dsPhase === 2
          ? [mmss ? { text: ctx.tr(pump ? 'ringPumping' : 'ringSoaking', { t: mmss }), tone: 'dim' } : null]
          : dsPhase === 3
            ? [{ text: ctx.tr('dialDsRefill'), tone: 'text' }]
            : dsPhase === 4
              ? [{ text: ctx.tr('dialDsRest'), tone: 'text' }]
              : dsPhase === 5
                ? [
                    { text: ctx.tr('dialDsFill'), tone: 'text' },
                    { text: `${ctx.tr('dialDsRinse')} ${pass}/2`, tone: 'cool' },
                  ]
                : [{ text: ctx.tr('dialDsRinse'), tone: 'dim' }];
    return water(
      'descale',
      ctx.tr('dialDescale'),
      'cool',
      value,
      null,
      rows,
      { type: 'segments', tone: 'cool', parts: programParts(DESCALE_ROUNDS, round, after) },
      after ? 1 : Math.max(0, round - 0.5) / DESCALE_ROUNDS,
    );
  }

  // the tank is empty: its screen comes first (selectScreen), a full ring in a dim water colour with the drop
  if (state === 70)
    return water(
      'tank',
      ctx.tr('dialTank'),
      'water',
      '',
      null,
      [{ text: ctx.tr('dialRefill'), tone: 'dim' }],
      { type: 'full', tone: 'water' },
      1,
      'drop',
    );

  if (state === 20 && (live?.rinse ?? op.rinse) === true) {
    // the rinse after a shot: a stopwatch that goes round once in its 10 s
    const t = Math.min(RINSE_MAX_S, seconds(20));
    return water(
      'rinse',
      ctx.tr('dialFlush'),
      'water',
      formatNumber(t, 1, ctx.lang),
      's',
      [temp],
      {
        type: 'arc',
        fill: t / RINSE_MAX_S,
        over: 0,
        tone: 'water',
        overTone: 'light',
        marker: t / RINSE_MAX_S,
      },
      t / RINSE_MAX_S,
    );
  }

  if (state === 25) {
    const stage = live?.warmup ?? op.warmup?.stage ?? 0;
    const pulses = Math.max(1, live?.pulses ?? op.warmup?.pulses ?? 1);
    const pulse = Math.min(pulses, Math.max(1, live?.pulse ?? op.warmup?.pulse ?? 1));
    // pulse k runs from (k − 1) · 23 s for 3 s, the pause after it until k · 23 s (the firmware's elapsed time)
    const t = seconds(25);
    const cycle = FLUSH_PULSE_S + FLUSH_PAUSE_S;
    const inPulse = clamp01((t - (pulse - 1) * cycle) / FLUSH_PULSE_S);
    const pause = stage === 3;
    if (pulses === 1) {
      // a single pulse (the app's "Spülen" with one pulse): the ring once round in its 3 s
      const f = pause ? 1 : inPulse;
      return water(
        'flush',
        ctx.tr('dialFlush'),
        'water',
        formatNumber(Math.min(FLUSH_PULSE_S, t), 1, ctx.lang),
        's',
        [temp],
        {
          type: 'arc',
          fill: f,
          over: 0,
          tone: 'water',
          overTone: 'light',
          marker: f,
        },
        f,
      );
    }
    // one segment per pulse, filling while it runs, so the ring is full with the last one
    const done = pulse - 1 + (pause || stage === 4 ? 1 : inPulse);
    const next = Math.ceil(pulse * cycle - t);
    return water(
      'flush',
      ctx.tr('dialFlush'),
      'water',
      `${pulse}/${pulses}`,
      null,
      [
        pause && next > 0 && next <= FLUSH_PAUSE_S
          ? { text: ctx.tr('flushNextIn', { s: next }), tone: 'dim' }
          : null,
        temp,
      ],
      {
        type: 'segments',
        tone: 'water',
        parts: Array.from({ length: pulses }, (_, i) => ({ fill: clamp01(done - i), current: false })),
      },
      done / pulses,
    );
  }

  const bf = op.backflush;
  const cycle = live?.bfc ?? bf?.cycle ?? 0;
  const bfDone = live?.bfd ?? bf?.done ?? false;
  if (state === 50) {
    const cycles = Math.max(1, live?.bfn ?? bf?.cycles ?? 1);
    const phase = live?.bfp ?? bf?.phase ?? 0;
    const cleaner = live?.clean ?? bf?.cleaner ?? 0;
    const ending = bfDone || phase === 3;
    const running = cycle > 0 && (phase === 1 || phase === 2);
    const left = backflushProgressNote(ctx.view, now, backflushTimes(ctx))?.vars?.s;
    const hint =
      cleaner === 1 && !ending
        ? { text: ctx.tr('dialCleaner'), tone: 'heat' as const }
        : cleaner === 3
          ? { text: ctx.tr('dialClearWater'), tone: 'cool' as const }
          : running && typeof left === 'number'
            ? { text: ctx.tr('dialLeft', { s: left }), tone: 'dim' as const }
            : null;
    return water(
      'backflush',
      ctx.tr('dialBackflush'),
      'brew',
      cycle > 0 || ending ? `${ending ? cycles : cycle}/${cycles}` : '',
      null,
      running
        ? [{ text: ctx.tr(phase === 1 ? 'dialBfFill' : 'dialBfFlush'), tone: 'dim' }, hint]
        : ending
          ? [{ text: ctx.tr('phaseDone'), tone: 'ready' }, hint]
          : [{ text: ctx.tr('dialSwitchOn'), tone: 'text' }, hint],
      { type: 'segments', tone: 'brew', parts: programParts(cycles, cycle, ending) },
      ending ? 1 : Math.max(0, cycle - 0.5) / cycles,
    );
  }

  return null;
}

/**
 * A note of the tab "Maschine" that says what the ring already shows (the backflush's cycle, the descaling's round):
 * left out there, nothing said twice. Notes with a step to take (refill, "Weiter") stay.
 */
export function ringShowsNote(ring: DialView | null, key: string): boolean {
  if (ring?.kind === 'backflush') return ['bfProgressLeft', 'bfActive', 'bfActiveBy'].includes(key);
  if (ring?.kind === 'descale') return ['dsCooling', 'dsRound', 'dsRest', 'dsRinse'].includes(key);
  return false;
}
